import ApiService from './apiService';

/**
 * Organization Service
 * Handles all organization-related API operations
 */
export interface Organization {
  id: string;
  name: string;
  domain: string;
  description?: string;
  logo?: string;
  members?: number;
  createdAt: string;
  updatedAt: string;
}

export const OrganizationService = {
  /**
   * Get all organizations for current user
   */
  getOrganizations: async () => {
    const response = await ApiService.get<Organization[]>('/orgs');
    // Backend returns array directly, wrap in expected format for consistency
    return { orgs: Array.isArray(response) ? response : [] };
  },

  /**
   * Get organization by ID
   */
  getOrganization: async (id: string) => {
    return ApiService.get<Organization>(`/orgs/${id}`);
  },

  /**
   * Create a new organization
   */
  createOrganization: async (data: {
    name: string;
    domain?: string;
    description?: string;
  }) => {
    return ApiService.post<Organization>('/orgs', { name: data.name });
  },

  /**
   * Update organization
   */
  updateOrganization: async (id: string, data: Partial<Organization>) => {
    return ApiService.put<Organization>(`/orgs/${id}`, data);
  },

  /**
   * Delete organization
   */
  deleteOrganization: async (id: string) => {
    return ApiService.delete(`/orgs/${id}`);
  },

  /**
   * Switch to a different organization
   */
  switchOrganization: async (organizationId: string) => {
    return ApiService.post<{
      arqiteToken: string;
      organizationId: string;
      organizationName: string;
    }>('/auth/switch-organization', { organizationId });
  },

  /**
   * Get organization members
   */
  getMembers: async (organizationId: string) => {
    return ApiService.get(`/orgs/${organizationId}/members`);
  },

  /**
   * Add member to organization
   */
  addMember: async (organizationId: string, email: string, role: string) => {
    return ApiService.post(`/orgs/${organizationId}/members`, {
      email,
      role
    });
  },

  /**
   * Remove member from organization
   */
  removeMember: async (organizationId: string, userId: string) => {
    return ApiService.delete(
      `/orgs/${organizationId}/members/${userId}`
    );
  },

  /**
   * Get organization settings
   */
  getSettings: async (organizationId: string) => {
    return ApiService.get(`/orgs/${organizationId}/settings`);
  },

  /**
   * Update organization settings
   */
  updateSettings: async (organizationId: string, settings: any) => {
    return ApiService.put(
      `/orgs/${organizationId}/settings`,
      settings
    );
  },
};

export default OrganizationService;
